import { currencies } from "@/lib/util"
import { Search } from "@mui/icons-material"
import ClearRoundedIcon from "@mui/icons-material/ClearRounded"
import {
  Box,
  Checkbox,
  FormGroup,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  RadioGroup,
  Select,
} from "@mui/material"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { Controller, useController } from "react-hook-form"
import {
  BpCheckedIcon,
  BpIcon,
  MenuProps,
  NumericFormatCustom,
  StyleRadioButton,
  StyledAutoComplete,
  StyledAutoCompleteField,
  StyledDatePicker,
  StyledFormControlLabel,
  StyledMenuItem,
  StyledSearchField,
  StyledSelectFormControl,
  StyledTextArea,
  StyledTextField,
} from "."
import { Calendar, ChevronDown, EyeClosed, EyeOpened } from "../SVGs"
import { Colors } from "../Theme/colors"
import { Fonts } from "../Theme/font"

export function CustomSearch({
  name,
  value,
  label,
  handleClearSearch,
  component: TextFieldComponent = StyledSearchField,
  ...rest
}) {
  return (
    <TextFieldComponent
      name={name}
      value={value}
      label={label}
      type="search"
      variant="outlined"
      fullWidth
      size="small"
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <IconButton disableRipple>
              <Search />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: value && (
          <IconButton
            disableRipple
            aria-label="toggle password visibility"
            onClick={handleClearSearch}
          >
            <ClearRoundedIcon />
          </IconButton>
        ),
      }}
      {...rest}
    />
  )
}
export function CustomizeSearch({
  name,
  value,
  label,
  component: TextFieldComponent = StyledSearchField,
  ...rest
}) {
  return (
    <TextFieldComponent
      name={name}
      value={value}
      label={label}
      type="search"
      variant="outlined"
      fullWidth
      size="small"
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Search />
          </InputAdornment>
        ),
      }}
      {...rest}
    />
  )
}
export function CustomTextField({
  type,
  name,
  control,
  helperText,
  error,
  label,
  multiple,
  component: TextFieldComponent = StyledTextField,
  ...rest
}) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      autocomplete={name}
      render={({ field }) => (
        <TextFieldComponent
          {...field}
          label={label}
          type={type}
          multiple={multiple}
          variant="outlined"
          fullWidth
          size="small"
          error={error}
          helperText={helperText}
          // InputLabelProps={{ shrink: true }}
          {...rest}
        />
      )}
    />
  )
}
export function CustomTextFieldWithIcon({
  type,
  name,
  control,
  helperText,
  error,
  label,
  endIcon,
  startIcon,
  component: TextFieldComponent = StyledTextField,
  rest,
}) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      autocomplete={name}
      render={({ field }) => (
        <TextFieldComponent
          {...field}
          label={label}
          type={type}
          variant="outlined"
          fullWidth
          size="small"
          error={error}
          helperText={helperText}
          // InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position={startIcon ? "start" : "end"}>
                {startIcon ? (
                  <div className="w-6 h-6 pl-2 border-r border-solid border-border flex justify-center items-center">
                    {startIcon}
                  </div>
                ) : (
                  <div className="w-6 h-6 pl-2 border-l border-solid border-border flex justify-center items-center">
                    {endIcon}
                  </div>
                )}
              </InputAdornment>
            ),
          }}
          {...rest}
        />
      )}
    />
  )
}

export function CustomNumberField({
  type,
  name,
  control,
  helperText,
  currency,
  error,
  label,
  component: TextFieldComponent = StyledTextField,
  ...rest
}) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      autocomplete={name}
      render={({ field }) => (
        <div className="w-full relative">
          <TextFieldComponent
            {...field}
            label={label}
            type={type}
            variant="outlined"
            fullWidth
            size="small"
            error={error}
            helperText={helperText}
            InputProps={{
              inputComponent: NumericFormatCustom,
              inputProps: {
                currency: currency,
              },
            }}
            {...rest}
          />
          <FormHelperText>{helperText}</FormHelperText>
        </div>
      )}
    />
  )
}

export function CurrencyAmountField({
  control,
  currencyName,
  amountName,
  label = "Amount",
  helperText,
  error,
  // autocomplete props
  options = currencies || [],
  getOptionLabel = (opt) => opt?.currency || "",
  isOptionEqualToValue = (option, value) =>
    value === undefined ||
    value?.code === undefined ||
    value === "" ||
    option?.code === value?.code,
  renderOption,
  filterOptions,
  filterSelectedOptions,
  // text field props
  type,
  component: TextFieldComponent = StyledTextField,
  requireCurrencyFirst = true,
  adornmentWidth = 160, // px
  className = "",
  ...rest
}) {
  const { field: currencyField } = useController({
    name: currencyName,
    control,
    defaultValue: "",
  })
  const { field: amountField } = useController({
    name: amountName,
    control,
    defaultValue: "",
  })

  const selectedCurrency = currencyField.value
  const disableAmount = requireCurrencyFirst && !selectedCurrency

  return (
    <div className={`w-full ${className}`}>
      <TextFieldComponent
        {...amountField}
        label={label}
        type={type}
        variant="outlined"
        fullWidth
        size="small"
        error={!!error}
        helperText={helperText}
        disabled={disableAmount}
        InputProps={{
          inputComponent: NumericFormatCustom,
          startAdornment: (
            <InputAdornment
              position="start"
              disablePointerEvents={false}
              sx={{ left: 0, ml: 0, pl: 0, mr: 1 }}
            >
              <div
                className="flex items-center"
                style={{ width: adornmentWidth, p: 0 }}
              >
                <StyledAutoComplete
                  value={selectedCurrency || null}
                  onChange={(_, val) => currencyField.onChange(val)}
                  onBlur={currencyField.onBlur}
                  options={options}
                  disableClearable={false}
                  size="small"
                  fullWidth
                  getOptionLabel={getOptionLabel}
                  isOptionEqualToValue={isOptionEqualToValue}
                  renderOption={renderOption}
                  filterOptions={filterOptions}
                  filterSelectedOptions={filterSelectedOptions}
                  popupIcon={
                    <ChevronDown
                      style={{ width: 18, height: 18, color: "#808080" }}
                    />
                  }
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderRight: "1px solid",
                      borderColor: "divider",
                      borderRadius: 0,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                      borderRight: "1px solid",
                      borderColor: Colors.primary,
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                      {
                        border: "none",
                        borderRight: "1px solid",
                        borderColor: Colors.borderColor,
                      },
                    "& .MuiInputBase-root.MuiOutlinedInput-root": {
                      paddingLeft: 0,
                    },
                    borderRadius: 0,
                  }}
                  renderInput={(params) => (
                    <StyledAutoCompleteField
                      {...params}
                      label=""
                      placeholder="Currency"
                      size="small"
                    />
                  )}
                />
              </div>
            </InputAdornment>
          ),
          inputProps: {
            currency:
              typeof selectedCurrency === "string"
                ? selectedCurrency
                : selectedCurrency?.code,
          },
        }}
        {...rest}
      />
      {helperText ? <FormHelperText>{helperText}</FormHelperText> : null}
    </div>
  )
}

export function CustomSelectField({
  type,
  name,
  control,
  helperText,
  error,
  id,
  options,
  label,
  labelId,
  component: SelectComponent = StyledSelectFormControl,
  ...rest
}) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      autocomplete={name}
      render={({ field }) => (
        <SelectComponent
          fullWidth
          variant="outlined"
          size="small"
          error={error}
        >
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            {...field}
            label={label}
            labelId={labelId}
            id={id}
            sx={{ pr: 1 }}
            MenuProps={MenuProps}
            IconComponent={(props) => {
              const rotate = props.className.toString().includes("iconOpen")
              return (
                <ChevronDown
                  style={{
                    width: 30,
                    height: 30,
                    color: "#808080",
                    transform: rotate ? "rotate(180deg)" : "none",
                  }}
                />
              )
            }}
            {...rest}
          >
            {options?.map((option, index) => {
              return (
                <StyledMenuItem
                  disableGutters
                  disableRipple
                  disableTouchRipple
                  key={option.value + index}
                  value={option.value}
                >
                  {option.label}
                </StyledMenuItem>
              )
            })}
          </Select>
          <FormHelperText>{helperText}</FormHelperText>
        </SelectComponent>
      )}
    />
  )
}
export function CustomAutoComplete({
  id,
  name,
  control,
  helperText,
  error,
  label,
  options,
  getOptionLabel,
  renderOption,
  multiple,
  filterSelectedOptions,
  isOptionEqualToValue,

  filterOptions,
  field,
  groupBy,
  ...rest
}) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={multiple ? [] : ""}
      autocomplete={name}
      render={({ field }) => (
        <StyledAutoComplete
          {...field}
          size="small"
          groupBy={groupBy}
          multiple={multiple}
          fullWidth
          options={options ? options : []}
          filterSelectedOptions={filterSelectedOptions}
          getOptionLabel={getOptionLabel}
          renderOption={renderOption}
          filterOptions={filterOptions}
          isOptionEqualToValue={isOptionEqualToValue}
          onChange={(_, data) => field.onChange(data)}
          // InputLabelProps={{ shrink: true }}
          popupIcon={
            <ChevronDown
              style={{
                width: 20,
                height: 20,
                color: "#808080",
              }}
            />
          }
          renderInput={(params) => (
            <StyledAutoCompleteField
              {...params}
              label={label}
              error={error}
              helperText={helperText}
            />
          )}
          {...rest}
        />
      )}
    />
  )
}
export function CustomPasswordField({
  type,
  name,
  control,
  helperText,
  error,
  label,
  handleClickShowPassword,
  handleMouseDownPassword,
  showPassword,
  component: TextFieldComponent = StyledTextField,
  rest,
}) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      autocomplete={name}
      render={({ field }) => (
        <TextFieldComponent
          {...field}
          label={label}
          type={type}
          variant="outlined"
          fullWidth
          size="small"
          error={error}
          helperText={helperText}
          // InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  disableRipple
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                  sx={{ mr: -1 }}
                >
                  {showPassword ? <EyeClosed /> : <EyeOpened />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          {...rest}
        />
      )}
    />
  )
}
export function CustomTextArea({
  name,
  control,
  helperText,
  error,
  label,
  component: TextAreaComponent = StyledTextArea,
  rest,
}) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue=""
      autocomplete={name}
      render={({ field }) => (
        <TextAreaComponent
          {...field}
          label={label}
          variant="outlined"
          fullWidth
          multiline
          error={error}
          // InputLabelProps={{ shrink: true }}
          helperText={helperText}
          {...rest}
        />
      )}
    />
  )
}

export function CustomDatePicker({
  name,
  control,
  helperText,
  disablePast,
  disableFuture,
  error,
  label,
  format,
  ...rest
}) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={""}
      render={({ field }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <StyledDatePicker
            {...field}
            label={label}
            name={name}
            control={field}
            format={format}
            disablePast={disablePast}
            disableFuture={disableFuture}
            // InputLabelProps={{ shrink: true }}
            slots={{
              openPickerIcon: () => (
                <Calendar
                  style={{
                    width: 20,
                    height: 20,
                    color: "#808080",
                  }}
                />
              ),
            }}
            slotProps={{
              textField: {
                helperText: helperText,
                error: error ? error : false,
                size: "small",
                fullWidth: true,
              },
              dialog: {
                sx: {
                  "& .MuiPaper-root": {
                    gap: "24px",
                    borderRadius: 1,
                    border: `1px solid ${Colors.secondaryLight}`,
                    boxShadow: "none",
                  },
                },
              },
              popper: {
                sx: {
                  "& .MuiPaper-root": {
                    gap: "24px",
                    borderRadius: 1,
                    border: `1px solid ${Colors.secondaryLight}`,
                    boxShadow: "none",
                  },
                },
              },
              calendarHeader: {
                sx: {
                  "& .MuiPickersCalendarHeader-root": {
                    display: "flex",
                    flexDirection: "column",
                  },
                  "& .MuiPickersCalendarHeader-label": {
                    font: `normal normal normal 600 14px/24px ${Fonts.primary}`,
                    color: Colors.secondary,
                    paddingRight: "7px",
                    textAlign: "center",
                    order: 3,
                  },
                  "& .MuiPickersCalendarHeader-labelContainer": {
                    display: "flex",
                    alignItems: "center",
                    justifyItems: "center",
                  },
                  "& .MuiPickersCalendarHeader-labelContainer:first-of-type": {
                    order: 0,
                    paddingRight: "20px",
                    paddingLeft: "20px",
                  },
                  "& .MuiPickersCalendarHeader-switchViewButton": {
                    display: "none",
                  },
                  "& .MuiPickersCalendarHeader-switcherButton": {
                    position: "relative",
                    right: 200,
                  },

                  "& .MuiPickersFadeTransitionGroup-root": {
                    display: "flex",
                    position: "absolute",
                    left: 110,
                  },
                  "& .MuiPickersArrowSwitcher-root": {
                    marginLeft: "-280px",
                  },

                  "& .MuiPickersArrowSwitcher-spacer": {
                    width: "232px",
                  },
                },
              },
              day: {
                sx: {
                  ":not(.Mui-selected)": {
                    padding: "2px 4px",
                    borderRadius: "4px",
                    font: `normal normal normal 400 14px/24px ${Fonts.primary}`,
                    color: Colors.secondary,
                    backgroundColor: Colors.light,
                    borderColor: Colors.light,
                  },
                  "&.MuiPickersDay-root.Mui-selected": {
                    padding: "2px 4px",
                    borderRadius: "4px",
                    backgroundColor: Colors.primary,
                    borderColor: Colors.primary,
                    font: `normal normal normal 700 14px/24px ${Fonts.primary}`,
                    color: Colors.light,
                    ":hover": {
                      color: Colors.light,
                      backgroundColor: Colors.primary,
                    },
                  },
                  ":hover": {
                    color: Colors.light,
                    backgroundColor: Colors.primary,
                  },
                  "&.MuiDayCalendar-weekDayLabel": {
                    padding: "2px 4px",
                    borderRadius: "4px",
                    font: `normal normal normal 700 14px/24px ${Fonts.primary} !important`,
                    color: Colors.secondary,
                  },
                  "& .MuiPickersDay-today": {
                    padding: "2px 4px",
                    borderRadius: "4px",
                    backgroundColor: Colors.light,
                    borderColor: Colors.primary,
                    font: `normal normal normal 700 14px/24px ${Fonts.primary}`,
                    color: Colors.secondary,
                  },
                },
              },
            }}
            {...rest}
          />
        </LocalizationProvider>
      )}
    />
  )
}

export function CustomRadio({ name, control, label, options }) {
  return (
    <>
      <FormLabel component="legend">{label}</FormLabel>
      <Controller
        name={name}
        control={control}
        defaultValue={""}
        render={({ field }) => (
          <RadioGroup>
            {options?.map((item, i) => (
              <StyleRadioButton
                {...field}
                value={item.value}
                label={item.label}
                key={"radio" + i}
              />
            ))}
          </RadioGroup>
        )}
      />
    </>
  )
}
// export function CustomCheckbox({ control, options, ...rest }) {
//   return (
//     <Box>
//       {options?.map((item, i) => {
//         return (
//           <FormGroup key={item.value + i}>
//             <StyledFormControlLabel
//               control={
//                 <Controller
//                   name={item.name}
//                   control={control}
//                   defaultValue={""}
//                   render={({ field }) => {
//                     return (
//                       <Checkbox
//                         {...field}
//                         sx={{
//                           "&:hover": {
//                             bgcolor: "transparent",
//                           },
//                         }}
//                         disableRipple
//                         color="default"
//                         checkedIcon={<BpCheckedIcon />}
//                         icon={<BpIcon />}
//                         disabled={rest?.disabled}
//                         inputRef={field.ref}
//                       />
//                     )
//                   }}
//                 />
//               }
//               label={item.label}
//             />
//           </FormGroup>
//         )
//       })}
//     </Box>
//   )
// }

export function CustomCheckbox({ control, checkOptions, disabled, ...rest }) {
  // Normalize `checkOptions` into an array for consistent mapping
  const options = Array.isArray(checkOptions)
    ? checkOptions
    : [checkOptions].filter(Boolean)

  // If there are no valid options, return nothing or a fallback UI
  if (!options.length) return null

  return (
    <Box {...rest}>
      {options.map((item, i) => (
        <FormGroup key={`${item.name}-${i}`}>
          <StyledFormControlLabel
            control={
              <Controller
                name={item.name}
                control={control}
                // This ensures the value is stored as a boolean in form state
                defaultValue={false}
                render={({ field }) => (
                  <Checkbox
                    // Instead of spreading all field props directly,
                    // we specifically map `checked` and `onChange` to a boolean.
                    checked={!!field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    color="default"
                    disabled={disabled}
                    inputRef={field.ref}
                    // Visual customization
                    icon={<BpIcon />}
                    checkedIcon={<BpCheckedIcon />}
                    sx={{
                      "&:hover": {
                        bgcolor: "transparent",
                      },
                    }}
                    disableRipple
                  />
                )}
              />
            }
            label={item.label}
          />
        </FormGroup>
      ))}
    </Box>
  )
}
